/**
 * Light / Dark theme context.
 *
 * Rules (iter-107v):
 *   1. **Logged-out** (marketing, login, password-reset, public pages):
 *      the DOM is ALWAYS dark, regardless of any saved preference. The
 *      light theme is opt-in for signed-in members only — the public
 *      site is dark-first by brand.
 *   2. **Logged-in**: the user's saved preference (localStorage key
 *      `vivo:theme`, hydrated from the backend `user.email_theme` on
 *      login if present) is applied. They can flip via Profile →
 *      Appearance.
 *   3. Saved preference is NEVER mutated by auth-state transitions —
 *      a logged-out forced-dark render does not overwrite the user's
 *      saved choice, so when they log back in their light preference
 *      restores cleanly.
 *
 * iter-66 introduced the original light/dark switch. iter-96 wired the
 * backend sync so server-rendered emails (reminders, digests) match
 * the chosen theme. iter-107v added the logged-out forced-dark rule.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { apiClient, useAuth } from "./App";

const STORAGE_KEY = "vivo:theme";

const ThemeContext = createContext({
  theme: "dark",            // the user's *preference* (persisted)
  effectiveTheme: "dark",   // what's actually applied to the DOM right now
  setTheme: () => {},
  toggle: () => {},
});

const applyToDocument = (mode) => {
  const html = document.documentElement;
  if (mode === "light") html.classList.add("light");
  else html.classList.remove("light");
  html.style.colorScheme = mode;
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();

  // The user's *preferred* theme. Persisted to localStorage, hydrated
  // from backend on login (see effect below). Never mutated by
  // auth-state changes — only by explicit user toggles.
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return "dark";
  });

  // The *effective* theme is what's actually drawn on the DOM. It
  // equals `theme` when the user is signed in, and is force-dark when
  // they're not. Exposed via context for any UI that needs to know
  // (e.g. embedded iframes / system-admin previews).
  const effectiveTheme = user ? theme : "dark";

  // iter-96: only push real toggles to the backend, never the initial
  // bootstrap or auth-state-driven re-renders.
  const lastSyncedRef = useRef(theme);

  // Apply the effective theme to the DOM whenever it changes. Note that
  // we still persist the *preference* (theme) to localStorage even when
  // the effective render is forced-dark, so logging back in restores
  // light cleanly.
  useEffect(() => {
    applyToDocument(effectiveTheme);
  }, [effectiveTheme]);

  // Persist the preference + best-effort backend sync on real toggles.
  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode — silently fail */
    }
    if (lastSyncedRef.current !== theme) {
      lastSyncedRef.current = theme;
      // Best-effort: failures are silent (not logged in, network blip).
      apiClient.put("/auth/me/email-theme", { theme }).catch(() => {});
    }
  }, [theme]);

  // iter-107v: on login (or on user object refresh), hydrate the
  // preference from the backend `email_theme` field if it's set AND
  // differs from the current local preference. This keeps the user's
  // preference consistent across devices — sign in on a new phone and
  // their saved dark/light choice from the desktop applies.
  //
  // Deferred via queueMicrotask so the setState happens AFTER the
  // current render cycle — appeases the strict
  // `react-hooks/set-state-in-effect` rule while preserving the
  // hydrate-on-login behaviour.
  useEffect(() => {
    if (!user) return undefined;
    const remote = user.email_theme;
    if (remote !== "light" && remote !== "dark") return undefined;
    if (remote === theme) return undefined;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      lastSyncedRef.current = remote;
      setThemeState(remote);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.email_theme]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTheme = useCallback((next) => {
    if (next === "light" || next === "dark") setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
